'use client';

import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { AverageAllPriceResponse } from '@/lib/api/data-contracts';
import { Droplet, Flame, Fuel, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { unwrap } from '@/lib/unwrap';

// 유종 코드별 설정
const PRODUCT_CONFIG: Record<
  string,
  { label: string; color: string; bgColor: string; icon: React.ElementType }
> = {
  B034: {
    label: '고급휘발유',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    icon: Fuel,
  },
  B027: {
    label: '보통휘발유',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    icon: Fuel,
  },
  D047: {
    label: '경유',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    icon: Droplet,
  },
  C004: {
    label: '등유',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    icon: Flame,
  },
  K015: {
    label: 'LPG',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    icon: Flame,
  },
};

// 유종 정렬 순서
const PRODUCT_ORDER = ['B034', 'B027', 'D047', 'C004', 'K015'];

interface MetricCardProps {
  data: AverageAllPriceResponse;
}

function MetricCard({ data }: MetricCardProps) {
  const config = PRODUCT_CONFIG[data.PRODCD || ''] || {
    label: data.PRODNM || '알 수 없음',
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    icon: Fuel,
  };

  const Icon = config.icon;
  const price = data.PRICE || 0;
  const diff = data.DIFF || 0;

  const getTrendIcon = () => {
    if (diff > 0) return <TrendingUp className="h-4 w-4 text-red-500" />;
    if (diff < 0) return <TrendingDown className="h-4 w-4 text-blue-500" />;
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  const getDiffColor = () => {
    if (diff > 0) return 'text-red-500';
    if (diff < 0) return 'text-blue-500';
    return 'text-gray-400';
  };

  const formatDiff = () => {
    if (diff > 0) return `+${diff.toFixed(2)}`;
    return diff.toFixed(2);
  };

  return (
    <Card className="overflow-hidden [container-type:inline-size]">
      <CardContent className="p-3">
        {/* 1줄: 아이콘 + 유종명 */}
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${config.bgColor}`}>
            <Icon className={`h-4 w-4 ${config.color}`} />
          </div>
          <p className="text-sm font-medium text-muted-foreground truncate">
            {config.label}
          </p>
        </div>
        {/* 2줄: 가격(좌) + 전일대비(우). 카드가 좁으면 가격 글자만 한 단계 줄여 한 줄을 유지한다 */}
        <div className="mt-1 flex items-center justify-between gap-2 whitespace-nowrap">
          <div className="flex items-baseline gap-1">
            <p className="text-xl font-bold [@container(max-width:230px)]:text-lg [@container(max-width:200px)]:text-base">
              {price.toLocaleString('ko-KR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
            <span className="text-sm text-muted-foreground">원/L</span>
          </div>
          <div className="flex flex-col items-end shrink-0">
            <div className="flex items-center gap-1">
              {getTrendIcon()}
              <span className={`text-sm font-medium ${getDiffColor()}`}>
                {formatDiff()}
              </span>
            </div>
            <span className="text-xs text-muted-foreground">전일대비</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MetricCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="flex flex-col items-end space-y-1">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-3 w-10" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function AveragePriceMetrics() {
  const { toast } = useToast();
  const [priceData, setPriceData] = useState<AverageAllPriceResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tradeDate, setTradeDate] = useState<string>('');

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('opinet/avg-all-price');
      const result = unwrap((await response.json()) as AverageAllPriceResponse[]);

      // 정렬된 데이터 설정
      const sortedData = result.sort((a, b) => {
        const aIndex = PRODUCT_ORDER.indexOf(a.PRODCD || '');
        const bIndex = PRODUCT_ORDER.indexOf(b.PRODCD || '');
        return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
      });

      setPriceData(sortedData);

      // 거래일자 설정 (첫 번째 데이터에서 추출)
      if (result.length > 0 && result[0].TRADE_DT) {
        const dt = result[0].TRADE_DT;
        // YYYYMMDD 형식을 YYYY.MM.DD 형식으로 변환
        const formatted = `${dt.slice(0, 4)}.${dt.slice(4, 6)}.${dt.slice(6, 8)}`;
        setTradeDate(formatted);
      }
    } catch {
      toast({
        title: '전국 평균 유가 로드 실패',
        description: '전국 평균 유가 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 대시보드 새로고침 이벤트 리스너
  useEffect(() => {
    const handleRefresh = () => {
      loadData();
    };

    window.addEventListener('dashboard-refresh', handleRefresh);
    return () => {
      window.removeEventListener('dashboard-refresh', handleRefresh);
    };
  }, [loadData]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
      {isLoading
        ? Array.from({ length: 5 }).map((_, i) => (
          <MetricCardSkeleton key={i} />
        ))
        : priceData.map(data => (
          <MetricCard key={data.PRODCD} data={data} />
        ))}
    </div>
  );
}
